import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sbp from '../../assets/images/sbp.png';
import m10Logo from '../../assets/images/m10.svg';
import { getAzPhoneNumberMask, getCurrencySymbol, getPhoneNumberMask } from '../../services/utils';
import MerchantBrandingBlock from '../../components/organisms/MerchantBrandingBlock/MerchantBrandingBlock';
import { Transaction } from '../../services/types';
import { useBankLogo } from '../../utils/bankLogos';
import Header from '../../components/organisms/Header/Header';
import Field from '../../components/atoms/Field/Field';
import InfoBlock from '../../components/organisms/InfoBlock/InfoBlock';
import Button from '../../components/atoms/Button/Button';
import Container from '../../components/atoms/Container/Container';
import RowFields from '../../components/atoms/RowFields/RowFields';
import styles from './SbpPage.module.scss';
import Labeled from '../../components/atoms/Labeled/Labeled';
import QR from '../../components/atoms/QR/QR';
import SupportButton from '../../components/atoms/SupportButton/SupportButton';
import useTheme from '../../hooks/useTheme';
import useIsDesktop from '../../hooks/useIsDesktop';
import AmountAlert from '../../components/organisms/AmountAlert/AmountAlert';
import { showsAmountChangeNotice } from '../../services/amountChange';
import type { DeclineContext } from '../../services/types';
import { ButtonGroup } from '../../components/ButtonGroup/ButtonGroup';

interface SbpPageProps {
  transaction: Transaction;
  timeLeft: string;
  ticketId: string;
  onCancel: () => void;
  /** Cancel as a refusal of the adjusted amount. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  onConfirm: () => void;
  setShowConfirm: Dispatch<SetStateAction<boolean>>;
}

const SbpPage: FC<SbpPageProps> = ({
  transaction,
  ticketId,
  onCancel,
  onDeclineOffer,
  onConfirm,
  timeLeft,
  setShowConfirm,
}) => {
  const { requisite, isRedirectUrlExist, amount } = transaction;
  // The template is Russian throughout; m10 texts come from i18n but not by device language,
  // or the screen would speak two languages at once.
  const { t } = useTranslation(undefined, { lng: 'ru' });
  const theme = useTheme();
  // m10 — кошелёк, а не СБП: номер адресует конкретный счёт в приложении, выдан под один депозит
  // и живёт по азербайджанскому формату. Отсюда свой заголовок, свой знак, своя инструкция и
  // предупреждение, которого у остальных телефонных методов нет.
  const isM10 = transaction.paymentMethod === 'M10AZN';
  // Знак у суммы — от валюты тикета, а не от метода: тот же метод может прийти в другой валюте.
  const currencySymbol = getCurrencySymbol(requisite.currency)?.trim();
  const [isProcessing, setIsProcessing] = useState(false);
  // Ссылка провайдера ведёт сразу в перевод в приложении банка. С компьютера её открывают
  // сканированием QR телефоном; на телефоне свой же экран не отсканировать — там кнопка.
  const isDesktop = useIsDesktop();
  const payUrl = requisite.qrCodeData;
  const showQr = !!payUrl && isDesktop;
  const showPayButton = !!payUrl && !isDesktop;

  const handleConfirmClick = async () => {
    if (isProcessing) return;
    if (transaction?.simpleConfirmation === false) {
      setShowConfirm(true);
      return;
    }
    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  const { src: bankLogoSrc, hasLogo: hasBankLogo } = useBankLogo(requisite.acquiringBank);

  const requesiteTitle = (type: string | undefined) => {
    switch (type) {
      case 'bank_account':
        return 'Номер счета';
      case 'bank_card':
        return 'Номер карты';
      case 'crypto_wallet':
        return 'Номер кошелька';
      case 'phone_number':
        return 'Номер телефона';
      default:
        return 'Номер реквизита';
    }
  };

  return (
    <>
      <Header
        transaction={transaction}
        ticketId={ticketId}
        timeLeft={timeLeft}
      />

      {transaction.merchant.toLowerCase() === 'vocxod' && (
        <div style={{ marginBottom: '28px' }}>
          <SupportButton userId={transaction.externalUserId} />
        </div>
      )}
      <Container>
        <MerchantBrandingBlock
          merchant={transaction.merchant}
          className={styles.imageWrapper}
        />
        <div className={styles.fieldGroup}>
          <Field
            image={isM10 ? m10Logo : sbp}
            bold
            text={
              isM10
                ? t('m10.transferByPhone')
                : showQr
                  ? 'Пополнение через СБП + QR'
                  : 'Пополнение через СБП'
            }
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <RowFields direction={showQr ? 'column' : 'row'}>
              {amount && (
                <Labeled width='100%'>
                  <span>Сумма перевода</span>
                  <Field text={amount} copy copyField='amount' currency={currencySymbol} />
                </Labeled>
              )}

              {/* Банка может не быть вовсе — у кошелька его нет. Пустая подпись «Банк»
                  читается как не догрузившийся экран, поэтому блока просто нет. */}
              {requisite?.acquiringBank && (
                <Labeled width='100%'>
                  <span>Банк</span>
                  {hasBankLogo ? (
                    <Field image={bankLogoSrc} />
                  ) : (
                    <Field
                      text={
                        requisite.acquiringBank.length > 15
                          ? requisite.acquiringBank.toUpperCase().slice(0, 17) +
                            '...'
                          : requisite.acquiringBank.toUpperCase()
                      }
                    />
                  )}
                </Labeled>
              )}
            </RowFields>
            {showQr && (
              <Labeled>
                <p>QR-код</p>
                <QR
                  data={payUrl}
                  bgColor={theme === 'dark' ? '#1E2B3E' : '#ffffff'}
                  fgColor={theme === 'dark' ? '#ffffff' : '#0e1827'}
                />
              </Labeled>
            )}
          </div>
          {!isRedirectUrlExist && (
            <Labeled>
              <span>{requesiteTitle('phone_number')}</span>
              <Field
                copy
                copyType='phone'
                copyField='requisite'
                text={isM10 ? getAzPhoneNumberMask(requisite?.phone) : getPhoneNumberMask(requisite?.phone)}
              />
            </Labeled>
          )}
          {transaction?.requisite?.country &&
            transaction.requisite?.country !== ' ' && (
              <Labeled>
                <span>Страна перевода</span>
                <Field text={transaction?.requisite?.country} />
              </Labeled>
            )}
          {requisite?.name && (
            <Labeled>
              <span>Получатель</span>
              <Field text={requisite?.name} />
            </Labeled>
          )}
          {isM10 && (
            <>
              <p className={styles.warnNote}>{t('m10.warning')}</p>
              <Labeled>
                <span>Инструкция</span>
                <ol className={styles.steps}>
                  <li>{t('m10.instructionStep1')}</li>
                  <li>{t('m10.instructionStep2')}</li>
                  <li>{t('m10.instructionStep3')}</li>
                </ol>
              </Labeled>
            </>
          )}
          {transaction?.simpleConfirmation === false && (
            <Labeled>
              <span>Инструкция</span>
              <InfoBlock data='' confirm={true} merchant={transaction?.merchant} />
            </Labeled>
          )}
          {transaction?.requisite?.userManual && (
            <Labeled>
              <span>Инструкция</span>
              <InfoBlock data={transaction?.requisite?.userManual} />
            </Labeled>
          )}
          {showPayButton && (
            <a className={styles.goLink} href={payUrl} target='_blank' rel='noopener noreferrer'>
              <Button variant='primary' dataSentryElement='leave-to-pay'>
                Оплатить в банке
              </Button>
            </a>
          )}
          <ButtonGroup>
            <Button
              variant='secondary'
              onClick={onCancel}
              disabled={isProcessing}
              dataSentryElement='cancel-ticket'
            >
              Отмена
            </Button>
            <Button
              onClick={handleConfirmClick}
              variant='primary'
              disabled={isProcessing}
              dataSentryElement='paid'
            >
              {isProcessing
                ? 'Проверяем платёж…'
                : isRedirectUrlExist
                ? 'Перейти к оплате'
                : 'Далее'}
            </Button>
          </ButtonGroup>
        </div>
        {showsAmountChangeNotice(transaction) && (
          <AmountAlert
            transaction={transaction}
            onDecline={() => onDeclineOffer('amount_change')}
          />
        )}
      </Container>
    </>
  );
};

export default SbpPage;
